/**
 * detection-environment.js
 */

import React from 'react';
import {
  Grid,
} from '@mui/material';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import get from 'lodash.get';
import { Types } from 'utils/constants';
import { sleep } from 'utils/misc';
import usePrevious from 'utils/use-previous';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import HiddenInput from 'components/form-inputs/hidden-input';
import Select from 'components/form-inputs/select';
import TextField from 'components/form-inputs/text-field';
import { DetectionOdorsContext } from '../context/detection-odors-context';
import DetectionExpander from './detection-expander';
import { OdorEnvironmentSection, OdorEnvironmentExpander } from './detection-odor-environment';
import { 
  sectionTypes, 
  defaultOdorEnvironment, 
  getEnvironmentIds, 
  getOdorIds, 
  odorlessEnvironmentExists,
  updateDefaultToCurrent 
} from './detection-common';


export function EnvironmentSection(props) {
  const {
    baseName,
    recordType,
    envIndex,
    environment,
    onDelete,
    formContext,
    addingFormContext,
    options,
    disabled,
    drawerState,
    translation
  } = props;
  const { setExpandedId } = React.useContext(DetectionOdorsContext)
  const { control, getValues, setValue } = formContext;
  const envType = useWatch({ control, name: `${baseName}[${envIndex}].type` });

  const { fields: odorEnvironments, append, remove } = useFieldArray({ 
    control, 
    name: `${baseName}[${envIndex}].odorEnvironments`,
    keyName: 'hookFormId' 
  });


  // add odor with env
  const doAdd = async () => {
    drawerState.loading.content.set(true);

    const newOEnvTemplate = get(addingFormContext.getValues(), baseName)[envIndex].odorEnvironments[0];
    const {odors: _, ...newOEnv} = newOEnvTemplate;

    setExpandedId();
    await sleep(0);
    append(newOEnv);
    await sleep(100);

    // update cached lists
    const structure = get(getValues(), baseName);
    setValue('detectionOdorlessEnvironmentExists', odorlessEnvironmentExists(structure));

    // update default values with newly-added odor-environment.
    const formNamePrefix = `${baseName}[${envIndex}].odorEnvironments[${odorEnvironments?.length ?? 0}]`; 
    updateDefaultToCurrent(formContext, formNamePrefix, newOEnv);

    drawerState.loading.content.set(false);
  }


  // remove odor with env
  const doRemove = (index) => {
    remove(index);

    // update cached lists after remove is complete
    setTimeout(() => {
      const structure = get(getValues(), baseName);
      setValue('detectionOdorlessEnvironmentExists', odorlessEnvironmentExists(structure), { shouldDirty: true });
      setValue('detectionOdorIds', getOdorIds(structure), { shouldDirty: true });
    });
  }


  return (
    <>
      <EnvironmentExpander
        id={environment.hookFormId1}
        recordType={recordType}
        baseName={baseName}
        isNew={false}
        envIndex={envIndex}
        environment={environment}
        onDelete={onDelete}
        formContext={formContext}
        mainControl={control}
        options={options}
        disabled={disabled}
        drawerState={drawerState}
        translation={translation}
      />
      {odorEnvironments?.filter(oEnv => oEnv.hookFormId)?.map((oEnv, odorEnvIndex) =>
        <OdorEnvironmentSection
          key={oEnv.hookFormId}
          recordType={recordType}
          baseName={baseName}
          odorEnvIndex={odorEnvIndex}
          envIndex={envIndex}
          odorEnvironment={oEnv}
          environment={environment}
          envTypeId={envType && envType.id}
          onDelete={index => doRemove(index)}
          options={options}
          formContext={formContext}
          addingFormContext={addingFormContext}
          mainControl={control}
          disabled={disabled}
          drawerState={drawerState}
          translation={translation}
        />)}
      <OdorEnvironmentExpander
        id={`${environment.hookFormId1}-new-oenv`}
        recordType={recordType}
        baseName={baseName}
        isNew={true}
        odorEnvIndex={0}
        envIndex={envIndex}
        odorEnvironment={defaultOdorEnvironment(options)}
        environment={environment}
        envTypeId={envType && envType.id}
        onAdd={doAdd}
        options={options}
        formContext={addingFormContext}
        disabled={disabled}
        drawerState={drawerState}
        translation={translation}
      />
    </>
  );
}


export function EnvironmentExpander(props) {
  const {
    id,
    // recordType,
    baseName,
    isNew,
    envIndex,
    environment,
    onAdd,
    onDelete,
    formContext,
    mainControl,
    forceError,
    disabled,
    drawerState,
    translation
  } = props;
  const formNamePrefix = `${baseName}[${envIndex}]`;
  const { t: gt } = useTranslation('general');
  const { t } = useTranslation(translation);
  const environmentTypes = React.useMemo(() => Types.Environment.entries(gt), [gt]);
  const { control, formState, getValues, setValue, trigger, resetField } = formContext;
  const envType = useWatch({ control, name: `${formNamePrefix}.type` });
  const envCount = useWatch({ control, name: `${formNamePrefix}.count` });
  const environmentIds = useWatch({ control: mainControl, name: 'detectionEnvironmentIds' });
  const prevEnvType = usePrevious(envType);
  const defaultEnvTypeId = environment?.type?.id;
  const envName = envType?.id ? gt(`types.environment.${envType.id}`) : '';
  const title = `${envCount} ${envName}${envName === 'Luggage' || parseInt(envCount) === 1 ? '' : 's'}`;
  const subTitle = 'Environment';

  
  // when type changes for existing env, recalculate exercise environment ids
  React.useEffect(() => {
    if (!isNew && envType && envType.id && prevEnvType && envType.id !== prevEnvType.id) {
      const structure = get(getValues(), baseName);
      setValue('detectionEnvironmentIds', getEnvironmentIds(structure));
    }
  }, [isNew, envType, prevEnvType, getValues, setValue, baseName]);


  // resets to given values or defaults if values is null
  const resetToValues = (values) => {
    resetField(`${formNamePrefix}.type`, { defaultValue: values?.type ?? undefined, keepDirty: true });
    resetField(`${formNamePrefix}.count`, { defaultValue: values?.count ?? undefined, keepDirty: true });
  }

  // is this type selected in this evironment
  const isTypeSelected = (typeId) => {
    return envType ? envType.id === typeId : false;
  };

  // is this type selected in any other environments
  const isTypeSelectedElsewhere = (typeId) => {
    if (!isTypeSelected(typeId) || isNew) {
      // no type is selected here, or this is a new environment, so check to see if the type is selected in any
      // non-new environments
      return Boolean(environmentIds && environmentIds.includes(typeId));
    } else {
      // this isn't a new environment and the type is selected here, so check to see if this type is selected
      // more than once in non-new environments
      return Boolean(environmentIds && environmentIds.filter(x => x === typeId).length > 1);
    }
  };


  // determines if given environment should be disabled
  const isEnvironmentDisabled = (typeId) => {
    return !isTypeSelected(typeId) && isTypeSelectedElsewhere(typeId);
  };


  // sets a new environment to its default state, i.e. blank count, and type from default provided, if allowed
  const resetNewEnvironment = () => {
    if (isNew) {
      resetToValues({
        type: defaultEnvTypeId && !isTypeSelectedElsewhere(defaultEnvTypeId)
          ? { id: defaultEnvTypeId }
          : { id: '' },
        count: '',
      });
    }
  };


  // for empty (new) environments, set the defaults
  React.useEffect(() => {
    if (isNew && defaultEnvTypeId && environmentIds) {
      resetNewEnvironment();
    }
    // don't include resetNewEnvironment as a dependency because it isn't a callback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, defaultEnvTypeId, environmentIds]);


  // done clicked or closing
  const onDone = async () => {
    // validate
    const isValid = await trigger([
      `${formNamePrefix}.type`,
      `${formNamePrefix}.count`,
    ]);

    if (isValid) {
      if (isNew) {
        await onAdd();
        resetToValues(environment);
      } else {
        updateDefaultToCurrent(formContext, formNamePrefix, getValues(formNamePrefix));
      }

      return true;
    }

    return false;
  }


  // on cancel
  const onCancel = () => {
    if (isNew) {
      resetNewEnvironment();
    } else {
      resetToValues(null);
    }
  };


  return (
    <DetectionExpander
      id={id}
      type={sectionTypes.ENVIRONMENT}
      typeTitle={subTitle}
      title={title}
      iconName='environment'      
      isNew={isNew}
      onDone={onDone}
      onCancel={onCancel}
      onDelete={() => onDelete(envIndex)}
      disabled={disabled}
      data-testtype='environment-expander'
      error={Boolean(
        forceError
        || get(formState.errors, baseName)?.[envIndex]?.type 
        || get(formState.errors, baseName)?.[envIndex]?.count
      )}
    >

      {/* environment id - hidden */}
      <HiddenInput
        formContext={formContext}
        name={`${formNamePrefix}.id`}
        defaultValue={environment.id}
        setValueAs={(value) => value ? parseInt(value) : null}
      />


      {/* environment type */}
      <Grid container>
        <FormGridItem xl={6}>
          <HelpTextItem
            sidebarText={drawerState.sidebarText}
            title={t('environments.environmentType.helpTitle')}
            text={t('environments.environmentType.helpInfo')}
            minimalHoverStyle
          >
            {focusHandlers => 
              <Select
                formContext={formContext}
                name={`${formNamePrefix}.type`}
                label={t('environments.environmentType.label')}
                defaultValue={environment.type}
                disabled={disabled}
                setValueAs={id => ({ id })}
                focusHandlers={focusHandlers}
                rules={{
                  validate: {
                    empty: val => Number.isInteger(val.id) || t('environments.environmentType.errMsgEmpty'),
                    duplicate: val => !isTypeSelectedElsewhere(val.id) || t('environments.environmentType.errMsgDuplicate'),
                  }
                }}
                options={environmentTypes.map(et => ({
                  ...et,
                  disabled: isEnvironmentDisabled(et.id)
                }))}
              />}
          </HelpTextItem>
        </FormGridItem>
      </Grid>


      {/* environment count */}
      <Grid container>
        <FormGridItem xl={6}>
          <HelpTextItem
            sidebarText={drawerState.sidebarText}
            title={t('environments.environmentCount.helpTitle')}
            text={t('environments.environmentCount.helpInfo')}
            minimalHoverStyle
          >
            {focusHandlers =>
              <TextField
                formContext={formContext}
                name={`${formNamePrefix}.count`}
                label={t('environments.environmentCount.label')}
                defaultValue={environment.count}
                type="number"
                focusHandlers={focusHandlers}
                helperText={t('environments.environmentCount.infoPartial')}
                rules={{
                  required: t('environments.environmentCount.errMsg'),
                  pattern: {
                    value: /^[1-9][0-9]*$/,
                    message: t('environments.environmentCount.errMsg')
                  }
                }}
              />}
          </HelpTextItem>
        </FormGridItem>
      </Grid>

    </DetectionExpander>
  );
}